import {controller, target, attr} from '@github/catalyst'

@controller
class StreamManagerElement extends HTMLElement {
  @target statusBadge: HTMLElement
  @target statusDot: HTMLElement
  @target vodNote: HTMLElement
  @target ingestHealth: HTMLElement
  @target viewers: HTMLElement
  @target viewersCount: HTMLElement
  @attr statsUrl = ''

  // Mux's verdict on the encoder's connection. "good" already means media time
  // is slipping half a second or more behind wallclock, so it reads as a
  // watch-this rather than an all-clear.
  private INGEST_STYLES: Record<string, string> = {
    excellent: 'text-emerald-400',
    good: 'text-amber-400',
    poor: 'text-rose-400',
    unknown: 'text-zinc-400',
  }

  private timer: number | null = null
  private POLL_INTERVAL = 10_000

  connectedCallback() {
    this.refresh()
    this.timer = window.setInterval(() => this.refresh(), this.POLL_INTERVAL)
  }

  disconnectedCallback() {
    if (this.timer !== null) window.clearInterval(this.timer)
    this.timer = null
  }

  private async refresh() {
    if (document.hidden || !this.statsUrl) return

    try {
      const response = await fetch(this.statsUrl, {headers: {Accept: 'application/json'}})
      if (!response.ok) return
      this.render(await response.json())
    } catch {
      // Leave the last known state on transient network errors.
    }
  }

  private render(stats: {
    status: string
    stream_ended: boolean
    vod_ready: boolean
    test_mode: boolean
    viewers: {views: number; viewers: number} | null
    ingest: {status: string; drift_ms: number | null; deviation_ms: number | null} | null
  }) {
    const [label, dotClass, textClass] = this.badgeFor(stats)
    // A test broadcast is live on the wire but going nowhere, so the badge
    // says so rather than reading as a real show in progress.
    this.statusBadge.textContent = stats.test_mode ? `${label} (test)` : label
    this.statusBadge.className = `text-sm ${textClass}`
    this.statusDot.className = `w-2 h-2 rounded-full ${dotClass}`
    this.vodNote.hidden = !(stats.stream_ended && stats.vod_ready)
    this.renderViewers(stats.viewers)
    this.renderIngest(stats.ingest)
  }

  // People, not player sessions: two tabs open by one ticket holder is one
  // viewer and two views. The sessions go in the tooltip when they disagree.
  private renderViewers(counts: {views: number; viewers: number} | null) {
    if (!this.viewers) return

    if (!counts) {
      this.viewers.hidden = true
      return
    }

    this.viewers.hidden = false
    this.viewersCount.textContent = String(counts.viewers)
    this.viewers.setAttribute(
      'data-tooltip',
      counts.views === counts.viewers
        ? `${counts.viewers} watching now`
        : `${counts.viewers} watching now across ${counts.views} players`,
    )
  }

  // Only shown while something is ingesting; the server sends null otherwise.
  private renderIngest(ingest: {status: string; drift_ms: number | null; deviation_ms: number | null} | null) {
    if (!this.ingestHealth) return

    if (!ingest) {
      this.ingestHealth.hidden = true
      this.ingestHealth.removeAttribute('data-tooltip')
      return
    }

    this.ingestHealth.hidden = false
    this.ingestHealth.textContent = `Ingest ${ingest.status}`
    this.ingestHealth.className = `text-sm ${this.INGEST_STYLES[ingest.status] || 'text-zinc-400'}`
    this.ingestHealth.setAttribute('data-tooltip', this.driftTooltip(ingest))
  }

  private driftTooltip(ingest: {drift_ms: number | null; deviation_ms: number | null}): string {
    const parts = []
    if (ingest.drift_ms !== null) parts.push(`${ingest.drift_ms}ms drift this session`)
    if (ingest.deviation_ms !== null) parts.push(`${ingest.deviation_ms}ms vs the last 30s`)
    return parts.length ? parts.join(' · ') : 'Encoder connection health'
  }

  private badgeFor(stats: {status: string; stream_ended: boolean}): [string, string, string] {
    if (stats.stream_ended) return ['Completed', 'bg-zinc-600', 'text-zinc-400']

    switch (stats.status) {
      case 'active':
      case 'recording':
        return ['Live', 'bg-green-400 animate-pulse', 'text-green-400']
      case 'connected':
        return ['Encoder connected', 'bg-amber-400', 'text-amber-400']
      case 'disconnected':
        return ['Reconnecting…', 'bg-amber-400 animate-pulse', 'text-amber-400']
      case 'disabled':
        return ['Disabled', 'bg-zinc-600', 'text-zinc-400']
      default:
        return ['Waiting for encoder', 'bg-zinc-600', 'text-zinc-400']
    }
  }
}
